import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './authentication/login/login.component';
import { BasketResolver } from './basket/resolver/basket.resolver';
import { OrderGridAdminComponent } from './orders/order-grid-admin/order-grid-admin.component';
import { OrderGridClientComponent } from './orders/order-grid-client/order-grid-client.component';
import { AdminOrdersResolver } from './orders/resolvers/admin-orders.resolver';
import { ClientOrdersResolver } from './orders/resolvers/client-orders.resolver';
import { HomeViewComponent } from './product-search/home-view/home-view.component';
import { ProductViewComponent } from './products/product-view/product-view.component';
import { ProductsResolver } from './products/resolvers/products.resolver';
import { AuthGuard } from './shared/guards/auth.guard';
import { CompanyResolver } from './company/resolvers/company.resolver';
import { CompaniesResolver } from './company/resolvers/companies.resolver';
import { CompanyGridComponent } from './company/company-grid/company-grid.component';
import { CompanyComponent } from './company/company/company.component';
import { BasketAdminComponent } from './basket/basket-admin/basket-admin.component';
import { BasketClientComponent } from './basket/basket-client/basket-client.component';
import { ProfileComponent } from './authentication/profile/profile.component';
import { ProfileResolver } from './authentication/resolver/profile.resolver';
import { PageNotFoundComponent } from './authentication/page-not-found/page-not-found.component';
import { PageForbiddenComponent } from './authentication/page-forbidden/page-forbidden.component';
import { BasketOfflineComponent } from './basket/basket-offline/basket-offline.component';
import { SettingsNavigationComponent } from './settings/settings-navigation/settings-navigation.component';
import { ManageDeviceCategoriesComponent } from './settings/manage-device-categories/manage-device-categories.component';
import { ManageDeviceManufacturersComponent } from './settings/manage-device-manufacturers/manage-device-manufacturers.component';
import { DeviceViewAdminComponent } from './devices/device-view-admin/device-view-admin.component';
import { DeviceViewClientComponent } from './devices/device-view-client/device-view-client.component';
import { DeviceDetailComponent } from './devices/device-detail/device-detail.component';
import { HistoryViewComponent } from './history/history-view/history-view.component';
import { InstallationViewAdminComponent } from './installations/installation-view-admin/installation-view-admin.component';
import { InstallationDetailComponent } from './installations/installation-detail/installation-detail.component';
import { InstallationViewClientComponent } from './installations/installation-view-client/installation-view-client.component';
import { ManageWearingPartsCategoriesComponent } from './settings/manage-wearing-parts-categories/manage-wearing-parts-categories.component';
import { RevisionsViewAdminComponent } from './revisions/revisions-view-admin/revisions-view-admin.component';
import { RevisionsViewClientComponent } from './revisions/revisions-view-client/revisions-view-client.component';
import { RevisionsDetailComponent } from './revisions/revisions-detail/revisions-detail.component';
import { ManageRevisionStatusComponent } from './settings/manage-revision-status/manage-revision-status.component';
import { CabinetDetailComponent } from './cabinet/cabinet-detail/cabinet-detail.component';
import { CabinetViewClientComponent } from './cabinet/cabinet-view-client/cabinet-view-client.component';
import { InventoryViewComponent } from './inventory/inventory-view/inventory-view.component';
import { InventoryDetailComponent } from './inventory/inventory-detail/inventory-detail.component';

const routes: Routes = [
  { 
    path: '', 
    redirectTo: '/scanner', 
    pathMatch: 'full' 
  },
  {
    path: "scanner",
    component: HomeViewComponent,
    data: { title: 'product-search.title', permission: 'Permissions.Products.View', iconName: 'qr-icon'},
    canActivate: [AuthGuard]
  },
  {
    path: "products",
    component: ProductViewComponent,
    data: { title: 'products.title', permission: 'Permissions.Products.SearchGlobal', iconName: 'article-icon', disableOffline: true},
    canActivate: [AuthGuard],
  },
  {
    path: "installations-admin",
    component: InstallationViewAdminComponent,
    data: { title: 'installations.title', permission: 'Permissions.Installations.SearchGlobal', iconName: 'installation-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "installations-admin/:id",
    component: InstallationDetailComponent,
    data: { permission: 'Permissions.Installations.SearchGlobal', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "installations",
    component: InstallationViewClientComponent,
    data: { title: 'installations.title', permission: 'Permissions.Installations.View', hideWithPermission:'Permissions.Installations.SearchGlobal', iconName: 'installation-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "installations/:id",
    component: InstallationDetailComponent,
    data: { permission: 'Permissions.Installations.View', disableOffline: true},
    canActivate: [AuthGuard]
  },  
  {
    path: "devices-admin",
    component: DeviceViewAdminComponent,
    data: { title: 'devices.title', permission: 'Permissions.Devices.SearchGlobal', iconName: 'device-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "devices-admin/:id",
    component: DeviceDetailComponent,
    data: { permission: 'Permissions.Devices.SearchGlobal', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "devices",
    component: DeviceViewClientComponent,
    data: { title: 'devices.title', permission: 'Permissions.Devices.Search', hideWithPermission:'Permissions.BasketItems.SearchGlobal', iconName: 'device-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "devices/:id",
    component: DeviceDetailComponent,
    data: { permission: 'Permissions.Devices.Search', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "inventory-admin",
    component: InventoryViewComponent,
    data: { title: 'inventory.title', permission: 'Permissions.InventoryDevices.SearchGlobal', iconName: 'inventory-icon', disableOffline: true, adminView: true},
    canActivate: [AuthGuard]
  },
  {
    path: "inventory-admin/:id",
    component: InventoryDetailComponent,
    data: { permission: 'Permissions.InventoryDevices.SearchGlobal', disableOffline: true, adminView: true},
    canActivate: [AuthGuard]
  },
  {
    path: "inventory",
    component: InventoryViewComponent,
    data: { title: 'inventory.title', permission: 'Permissions.InventoryDevices.Search', hideWithPermission:'Permissions.InventoryDevices.SearchGlobal', requiresCompanyFeature: 'inventoryEnabled', iconName: 'inventory-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "inventory/:id",
    component: InventoryDetailComponent,
    data: { permission: 'Permissions.InventoryDevices.Search', requiresCompanyFeature: 'inventoryEnabled', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "revisions-admin",
    component: RevisionsViewAdminComponent,
    data: { title: 'revisions.title', permission: 'Permissions.Revisions.SearchGlobal', iconName: 'revision-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "revisions-admin/:id",
    component: RevisionsDetailComponent,
    data: { permission: 'Permissions.Revisions.SearchGlobal', disableOffline: true},
    canActivate: [AuthGuard]
  },
  /*
  {
    path: "revisions",
    component: RevisionsViewClientComponent,
    data: { title: 'revisions.title', permission: 'Permissions.Revisions.Search', hideWithPermission:'Permissions.Revisions.SearchGlobal', iconName: 'revision-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "revisions/:id",
    component: RevisionsDetailComponent,
    data: { permission: 'Permissions.Revisions.Search', disableOffline: true},
    canActivate: [AuthGuard]
  },
  */
  {
    path: "companies",
    component: CompanyGridComponent,
    data: { title: 'companies.title', permission: 'Permissions.Companies.SearchGlobal', iconName: 'company-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "companies/:id",
    component: CompanyComponent,
    data: { permission: 'Permissions.Companies.View', disableOffline: true},
    canActivate: [AuthGuard],
    resolve: {
      company: CompanyResolver
    }
  },
  {
    path: "basket-offline",
    component: BasketOfflineComponent,
    data: { title: 'basket-offline.title', permission: 'Permissions.BasketItems.Create', iconName: 'basket-icon', hideOnline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "basket",
    component: BasketClientComponent,
    data: { title: 'basket.title', permission: 'Permissions.BasketItems.Create', hideWithPermission:'Permissions.BasketItems.SearchGlobal', iconName: 'basket-icon', hideOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "basket-admin",
    component: BasketAdminComponent,
    data: { title: 'basket.admin.title', permission: 'Permissions.BasketItems.SearchGlobal', iconName: 'basket-icon', hideOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "orders",
    component: OrderGridClientComponent,
    data: { title: 'orders.client.title', permission: 'Permissions.Orders.View', hideWithPermission:'Permissions.Orders.Update', iconName: 'order-icon', disableOffline: true},
    canActivate: [AuthGuard],
    resolve: {
      orders: ClientOrdersResolver
    }
  },
  {
    path: "orders-admin",
    component: OrderGridAdminComponent,
    data: { title: 'orders.admin.title', permission: 'Permissions.Orders.Update', iconName: 'order-icon', disableOffline: true},
    canActivate: [AuthGuard],
    resolve: {
      orders: AdminOrdersResolver
    }
  },
  {
    path: "profile",
    component: ProfileComponent,
    data: { title: 'profile.title', permission: 'Permissions.Products.View', iconName: 'profile-icon', disableOffline: true},
    canActivate: [AuthGuard],
    resolve: {
      profile: ProfileResolver
    }
  },
  {
    path: "settings",
    component: SettingsNavigationComponent,
    data: { title: 'settings.title', permission: 'Permissions.Settings.View', iconName: 'settings-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "settings/device-categories",
    component: ManageDeviceCategoriesComponent,
    data: { permission: 'Permissions.DeviceCategories.View', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "settings/device-manufacturers",
    component: ManageDeviceManufacturersComponent,
    data: { permission: 'Permissions.DeviceManufacturers.View', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "settings/wearing-part-categories",
    component: ManageWearingPartsCategoriesComponent,
    data: { permission: 'Permissions.WearingPartCategories.Search', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "settings/revision-status",
    component: ManageRevisionStatusComponent,
    data: { permission: 'Permissions.RevisionStatus.Search', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "history",
    component: HistoryViewComponent,
    data: { title: 'history.title', permission: 'Permissions.History.View', iconName: 'history-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "companies/:companyId/cabinets-admin/:cabinetId",
    component: CabinetDetailComponent,
    data: { permission: 'Permissions.Cabinets.SearchGlobal', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "cabinets",
    component: CabinetViewClientComponent,
    data: { title: 'cabinets.title', permission: 'Permissions.Cabinets.Search', hideWithPermission:'Permissions.Cabinets.SearchGlobal', iconName: 'cabinet-icon', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "cabinets/:cabinetId",
    component: CabinetDetailComponent,
    data: { permission: 'Permissions.Cabinets.Search', disableOffline: true},
    canActivate: [AuthGuard]
  },
  {
    path: "auth",
    component: LoginComponent,
  },
  {
    path: "403",
    component: PageForbiddenComponent
  },
  {
    path: "404",
    component: PageNotFoundComponent
  },
  {
    path: "**",
    component: PageNotFoundComponent
  },
  
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
